import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SITE } from "../../data/site";
import { LotusRule, TempleGateLogo } from "../Ornaments";

/*
 * Sign-in layout for the staff portals (library admin, CMS admin) in the public site's heritage
 * design: a leather panel with the brand and Gurudev's portrait beside a parchment sign-in card.
 * Styles: site/styles/auth.css (scoped under .jbs-site like the rest of the site).
 */

type Props = {
  /** small label above the panel headline, e.g. "Library Administration" */
  eyebrow: string;
  /** panel headline; the second line is set in italic gold */
  headline: [string, string];
  /** one sentence under the headline */
  intro: string;
  /** card icon (lucide) */
  icon: ReactNode;
  title: string;
  subtitle: string;
  children: ReactNode;
};

export function AuthShell({
  eyebrow,
  headline,
  intro,
  icon,
  title,
  subtitle,
  children,
}: Props) {
  // Toasts render outside this page (in the app-wide toaster); this class lets
  // auth.css give them the heritage look only while a sign-in page is open.
  useEffect(() => {
    document.documentElement.classList.add("jbs-auth");
    return () => document.documentElement.classList.remove("jbs-auth");
  }, []);

  return (
    <div className="jbs-site au-page">
      <aside className="au-panel" aria-label={SITE.name}>
        <Link to="/" className="au-brand" aria-label={`${SITE.name} – home`}>
          <TempleGateLogo className="au-brand-gate" />
          <span className="au-brand-text">
            <span className="au-brand-name">{SITE.name}</span>
            <LotusRule className="au-brand-rule" />
          </span>
        </Link>

        <div className="au-panel-body">
          <p className="au-eyebrow">{eyebrow}</p>
          <h1 className="au-headline">
            {headline[0]}
            <em>{headline[1]}</em>
          </h1>
          <p className="au-intro">{intro}</p>

          <figure className="au-portrait">
            <img
              src="/images/gurudev/portrait.webp"
              alt="Gurudev Muni Jambuvijayaji Maharaj Saheb"
              loading="eager"
            />
          </figure>
        </div>

        <p className="au-panel-foot">
          Muni Jambuvijayaji Research Center · Adani Shantigram
        </p>
      </aside>

      <main className="au-main">
        <div className="au-card">
          <span className="au-card-icon" aria-hidden="true">
            {icon}
          </span>
          <h2 className="au-title">{title}</h2>
          <p className="au-subtitle">{subtitle}</p>
          <LotusRule className="au-rule" />

          {children}

          <Link to="/" className="au-back">
            <ArrowLeft aria-hidden="true" />
            Return to main website
          </Link>
        </div>
      </main>
    </div>
  );
}

/** Label + input with a leading icon and an optional trailing control (e.g. show password). */
export function AuthField({
  id,
  label,
  icon,
  action,
  trailing,
  children,
}: {
  id: string;
  label: string;
  icon: ReactNode;
  /** small control on the label row, e.g. "Forgot?" */
  action?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="au-field">
      <div className="au-label-row">
        <label htmlFor={id}>{label}</label>
        {action}
      </div>
      <div className={`au-control${trailing ? " has-trailing" : ""}`}>
        <span className="au-control-icon" aria-hidden="true">
          {icon}
        </span>
        {children}
        {trailing}
      </div>
    </div>
  );
}
